import * as React from "react";

export type DDSIconProps = React.SVGProps<SVGSVGElement> & {
  /** @deprecated - wrap in a `<DDSButton variant="ghost">` put the onClick on there */
  onClick?: never;
  size?: "xs" | "s" | "m" | "l" | "xl" | "xxl";
};

export type DDSIcon = (props: DDSIconProps) => React.ReactElement;

/** Every committed sprite uses this box; `check-sprite` enforces it. */
const VIEW_BOX = "0 0 24 24";

/**
 * Build an icon component that renders its glyph from the icon's own sprite.
 *
 * There is one delivery path: `<use href>` against a single-symbol sprite file.
 * The flag-gated inline path and the 262 generated components that carried it are
 * gone — `src/index.ts` is now a flat mapping of one `makeIcon` call per icon, so
 * adding an icon is one generated line and one generated `.svg`.
 *
 * `spriteUrl` is passed in rather than derived from `id` because
 * `new URL(<literal>, import.meta.url)` only survives bundling when the literal sits
 * in the module that ships. That form is what makes the sprite a build-time asset:
 * Vite and webpack both rewrite it to a content-hashed URL served from the app's own
 * origin, which matters because external `<use href>` is same-origin restricted and
 * is not reliably CORS-negotiable. Never build one of these URLs at runtime — a
 * runtime-assembled path resolves to nothing once filenames are hashed.
 *
 * `viewBox` is hard-coded rather than read per symbol because every symbol uses the
 * same box and `check-sprite` fails the build if one does not.
 */
export const makeIcon = (id: string, spriteUrl: string): DDSIcon => {
  const href = `${spriteUrl}#${id}`;

  const Icon = ({ size, onClick: _onClick, ...otherProps }: DDSIconProps) => (
    <svg
      {...otherProps}
      className={`ddsIcon ${size || ""} ${otherProps.className || ""}`}
      viewBox={VIEW_BOX}
      xmlns="http://www.w3.org/2000/svg"
    >
      <use href={href} />
    </svg>
  );
  Icon.displayName = `Icon(${id})`;

  return Icon;
};
